import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/components/AuthContext';

export interface KnowledgeAsset {
  id: string;
  title: string;
  title_local: string | null;
  description: string;
  description_local: string | null;
  type: string;
  media_url: string | null;
  thumbnail_url: string | null;
  media_duration_seconds: number | null;
  media_size_bytes: number | null;
  sort_order: number | null;
  tags: string[] | null;
  activity_tag: string | null;
  production_stage: string | null;
  language: string;
  country_id: string;
  crop_id: string | null;
  is_active: boolean | null;
  created_at: string | null;
}

export function useKnowledgeAssets() {
  const { user } = useAuth();

  // Fetch profile directly from Supabase (avoids Zustand hydration race)
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile-for-kb', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('country_id, primary_crop_id')
        .eq('id', user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 30,
  });
  const countryId = profile?.country_id;
  const cropId = profile?.primary_crop_id;

  const assetsQuery = useQuery({
    queryKey: ['knowledge-assets', countryId, cropId],
    queryFn: async (): Promise<KnowledgeAsset[]> => {
      if (!countryId) return [];

      // SECURITY DEFINER fn applies: country + active + type IN (video, youtube_video)
      // + programme scope (programme_id IS NULL OR farmer enrolled via dap_disbursements)
      // + crop scope (crop matches OR crop is null). Farmers cannot self-read
      // dap_disbursements under existing RLS, so the join lives inside the definer fn.
      const { data, error } = await supabase
        .rpc('get_visible_knowledge_assets', {
          p_country_id: countryId,
          p_crop_id: cropId ?? null,
        });

      if (error) {
        console.error('Failed to fetch knowledge assets:', error);
        return [];
      }
      return (data as KnowledgeAsset[]) || [];
    },
    enabled: !!user && !!countryId,
    staleTime: 5 * 60 * 1000,
  });

  // Combine loading states: profile loading OR assets loading
  return {
    ...assetsQuery,
    isLoading: profileLoading || assetsQuery.isLoading,
  };
}
